import {
  buffer,
  featureCollection,
  featureReduce,
  getCoords,
  getType,
} from '@turf/turf';
import {
  Feature,
  FeatureCollection,
  LineString,
  MultiPolygon,
  Polygon,
} from 'geojson';
import { feetPerMile } from '../constants/units';
import lineOffset from './betterLineOffset';
import { geometryTypes, headlandSectionTypes } from '../constants/fields';
import { DEFAULT_LAYER_WIDTH_FEET } from '../constants/layers';

interface CustomGeoJsonProperties {
  width?: number;
  isInnerFeature?: boolean;
}

// We set properties.type to this value to be able to adjust layer/style properties for secondary features.
export const SECONDARY_FEATURE_TYPE = 'secondary';

export const unaugmented = ({ data }: { data?: FeatureCollection } = {}) =>
  data;

export const getSecondaryHeadlandFeature = (
  feature: Feature<Polygon, CustomGeoJsonProperties>,
  width: number,
) => {
  const {
    // Set default width here, because if width is null, function default parameter is ignored
    width: featureWidth = width ?? DEFAULT_LAYER_WIDTH_FEET,
    isInnerFeature,
  } = feature.properties;
  const widthInMiles = featureWidth / feetPerMile;
  if (
    getType(feature) !== geometryTypes.POLYGON ||
    feature.geometry.coordinates[0].length < 4
  ) {
    return null;
  }
  const secondaryFeature = buffer(
    feature,
    isInnerFeature ? widthInMiles : -widthInMiles,
    {
      units: 'miles',
    },
  );
  return secondaryFeature
    ? {
        ...secondaryFeature,
        properties: {
          ...secondaryFeature.properties,
          isInnerFeature: !isInnerFeature,
          type: SECONDARY_FEATURE_TYPE,
        },
      }
    : null;
};

export const augmentHeadlands = ({
  data,
  width,
}: {
  data?: FeatureCollection<Polygon, CustomGeoJsonProperties>;
  width: number;
}) => {
  if (!data) return null;
  const augmented = featureReduce(
    data,
    (result, feature) => {
      const secondaryFeature = getSecondaryHeadlandFeature(feature, width);
      return [
        ...result,
        feature,
        ...(secondaryFeature ? [secondaryFeature] : []),
      ];
    },
    <Feature<Polygon | MultiPolygon>[]>[],
  );
  return featureCollection(augmented);
};

// Augments the field feature for section headlands
// Returns featureCollection([innerFeature, outerFeature])
// If the section does not support a headland, if the headlandWidth is zero, or
// if the secondary feature is null, then innerFeature and outerFeature are both
// the original fieldFeature
export const augmentFieldFeature = ({
  fieldFeature,
  headlandInnerFeature,
  headlandWidth,
  type,
}: {
  fieldFeature: Feature<Polygon>;
  headlandInnerFeature: boolean;
  headlandWidth: number;
  type: string;
}) => {
  if (!fieldFeature) return null;
  if (!headlandSectionTypes.some((t) => t === type) || !headlandWidth) {
    return featureCollection([fieldFeature, fieldFeature]);
  }
  const fieldFeatureWithProps = {
    ...fieldFeature,
    properties: {
      ...fieldFeature.properties,
      isInnerFeature: headlandInnerFeature,
    },
  };
  const secondaryFeature = getSecondaryHeadlandFeature(
    fieldFeatureWithProps,
    headlandWidth,
  );
  if (!secondaryFeature) return featureCollection([fieldFeature, fieldFeature]);
  return featureCollection(
    headlandInnerFeature
      ? [fieldFeatureWithProps, secondaryFeature]
      : [secondaryFeature, fieldFeatureWithProps],
  );
};

export const augmentRoads = ({
  data,
  width,
}: {
  data?: FeatureCollection<LineString>;
  width: number;
}) => {
  if (!data) return null;
  const augmented = featureReduce(
    data,
    (result, feature) => {
      // Set default width here, because if width is null, function default parameter is ignored
      const featureWidth =
        feature.properties?.width || width || DEFAULT_LAYER_WIDTH_FEET;
      const widthInMiles = featureWidth / feetPerMile / 2;
      if (
        getType(feature) !== geometryTypes.LINE_STRING ||
        getCoords(feature).length < 2
      ) {
        return [...result, feature];
      }

      const centerLine = {
        ...feature,
        properties: { ...feature.properties, style: 'center' },
      };

      const getOffsetLine = (offset: number) => ({
        ...lineOffset(feature, offset, {
          units: 'miles',
        }),
        properties: { ...feature.properties, type: SECONDARY_FEATURE_TYPE },
      });

      const line1 = getOffsetLine(-widthInMiles);
      const line2 = getOffsetLine(widthInMiles);

      return [...result, centerLine, line1, line2];
    },
    <Feature<LineString>[]>[],
  );
  return featureCollection(augmented);
};
